import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { useAtomValueLoadable } from 'src/utils/jotai'
import styled from '@emotion/styled'
import { Search } from '@emotion-icons/fa-solid/Search'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { LocationArrow } from '@emotion-icons/fa-solid/LocationArrow'
import { Colors, Levels, Shapes } from 'src/components/theme'
import { Spinner } from 'src/components/spinner'
import { SearchInput, SearchOption } from 'src/v2/components/sidebar-search/search-input'
import { SearchAction } from 'src/v2/components/sidebar-search/search-action'
import { SearchOptions, useSearch, useSearchSuggestions } from 'src/views/map-explorer/sidebar/search/use-search'
import { MapExplorerSidebarState } from 'src/v2/state/map-explorer/sidebar'
import { fullAddress } from 'src/utils/full-address'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { useMapExplorerChoice } from 'src/views/map-explorer/sidebar/search/use-choice'
import { SEARCH_RESULT_TYPE_ENUM } from 'src/common-services'
import { checkIfResultsAreCompletelyEmpty } from 'src/views/map-explorer/sidebar/panels/results/search-result-items'

import * as searchAtoms from 'src/v2/state/map-explorer/search'
import * as sidebatAtoms from 'src/v2/state/map-explorer/sidebar'
import * as coreAtoms from 'src/v2/state/core'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import { formatString } from 'src/utils/format-string'
import { parcelFullAddress } from 'src/views/map-explorer/sidebar/panels/selected-parcel'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[4]}px;
  background-color: ${Colors.primaryBlue500};
  width: 100%;
  z-index: ${Levels.googleSearchPanel};
`

const SEARCH_FLOAT_STATES: MapExplorerSidebarState[] = [
  'SELECTED_ADDRESS',
  'SELECTED_LOCATION',
  'SELECTED_SHOPPING_CENTER',
  'SELECTED_BRAND',
  'SELECTED_PARCEL',
  'SELECTED_OWNER',
]

export const DEFAULT_MAP_EXPLORER_SEARCH_OPTIONS: SearchOptions = {
  resultTypes: [
    SEARCH_RESULT_TYPE_ENUM.Enum.address,
    SEARCH_RESULT_TYPE_ENUM.Enum.location,
    SEARCH_RESULT_TYPE_ENUM.Enum['shopping center'],
    // In the future we may support other result types
    // SEARCH_RESULT_TYPE_ENUM.Enum.brand,
    // SEARCH_RESULT_TYPE_ENUM.Enum.parcel,
    // SEARCH_RESULT_TYPE_ENUM.Enum.owner,
  ],
}

export const MapExplorerSearch = () => {
  const selectedEntity = useAtomValueLoadable(coreAtoms.selectedEntity)
  const selectedState = useAtomValueLoadable(sidebatAtoms.selectedEntityState)
  const [query, setQuery] = useAtom(searchAtoms.searchQuery)
  const [suggestions, setSuggestions] = useState<NonNullable<SearchOption>[]>([])
  const { search, clear } = useSearch(DEFAULT_MAP_EXPLORER_SEARCH_OPTIONS)
  const { getSearchSuggestions } = useSearchSuggestions()
  const [loading, setLoading] = useAtom(searchAtoms.isSearching)
  const results = useAtomValueLoadable(searchAtoms.searchResults)
  const inEntitySelector = Boolean(useAtomValue(selectorAtoms.supportedSelections))
  const { clearChoice } = useMapExplorerChoice()
  const { clearSelected } = useMapExplorerNavigation()

  const showBackToResults = useMemo(() => {
    if (loading || results.state === 'loading') {
      return false
    }

    return (
      results.state === 'hasData' &&
      results.data &&
      !checkIfResultsAreCompletelyEmpty(results.data) &&
      selectedState.state === 'hasData' &&
      selectedState.data !== 'EMPTY'
    )
  }, [loading, results, selectedState])

  const shouldSearchFloat = useMemo(() => {
    return (
      !loading &&
      (selectedState.state === 'loading' ||
        (selectedState.state === 'hasData' && SEARCH_FLOAT_STATES.includes(selectedState.data)))
    )
  }, [selectedState, loading])

  const suggestionOptions = useMemo(() => {
    return suggestions.map((suggestion) => ({
      ...suggestion,
      icon: suggestion.id.includes('political') ? (
        <LocationArrow size={16} color={Colors.neutral400} />
      ) : suggestion.id.includes('place_id') ? (
        <MapMarkerAlt size={16} color={Colors.neutral400} />
      ) : undefined,
    }))
  }, [suggestions])

  const onFocus = useCallback(async () => {
    if (!!!query) {
      setSuggestions([])
      return
    }
    const searchSuggestions = await getSearchSuggestions(query)
    setSuggestions(searchSuggestions)
  }, [query])

  const onInputChange = useCallback(
    async (value: string) => {
      setQuery(value)
      const searchSuggestions = await getSearchSuggestions(value)
      setSuggestions(searchSuggestions)
    },
    [query]
  )

  const onChange = useCallback(
    async (option: SearchOption) => {
      if (option?.value) {
        setLoading(true)
        await search(option.value)
        setQuery(option.value)
        setLoading(false)
      }
    },
    [suggestionOptions]
  )

  const onSearch = async () => {
    setLoading(true)
    await search(query)
    setLoading(false)
  }

  const onClear = async () => {
    setQuery('')
    await clear()
  }

  const onBackToResults = async () => {
    if (results.state === 'hasData') {
      inEntitySelector ? clearChoice() : await clearSelected({ keepSearchResults: true })
    }
  }

  // Sync search query depending on the state of the selected entity
  useEffect(() => {
    if (selectedEntity.state !== 'hasData') {
      return
    }
    const { data: entity } = selectedEntity
    if (!entity) {
      return
    }
    // Sync selected entity with search query depending on the state of the selected entity

    // Location use name + address
    if (entity.type === 'SELECTED_LOCATION' || entity.type === 'SELECTED_SHOPPING_CENTER') {
      setQuery(`${entity.data['am-loc'].name} ${fullAddress(entity.data['am-loc'])}`)
      return
    }
    // Address use address only
    if (entity.type === 'SELECTED_ADDRESS') {
      setQuery(entity.data.formatted_address || 'waw')
      return
    }
    // Brand use brand name only
    if (entity.type === 'SELECTED_BRAND') {
      setQuery(entity.data.name)
      return
    }
    // Parcel use address only
    if (entity.type === 'SELECTED_PARCEL') {
      const address = `${formatString(parcelFullAddress(entity.data.address, entity.data.city, entity.data.state || '', entity.data.zip), 'address')}`
      setQuery(address)
      return
    }
    // Owner use name only
    if (entity.type === 'SELECTED_OWNER') {
      setQuery(formatString(entity.data.name, 'name'))
      return
    }
  }, [selectedEntity])

  return (
    <Wrapper
      style={{
        display: selectedState.state === 'hasData' && selectedState.data === 'SELECTED_AREA' ? 'none' : 'block',
        position: shouldSearchFloat ? 'absolute' : 'relative',
        background: shouldSearchFloat
          ? `linear-gradient(180deg, ${Colors.neutral950} 0%, rgba(0, 0, 0, 0) 100%)`
          : Colors.primaryBlue500,
      }}>
      <SearchInput
        disabled={loading || results.state === 'loading'}
        icon={loading || results.state === 'loading' ? <Spinner size={16} /> : <Search size={16} />}
        search={query || ''}
        placeholder="Search for an address / location / brand"
        onFocus={onFocus}
        onInputChange={onInputChange}
        onChange={onChange}
        onEnter={onSearch}
        onClear={onClear}
        options={suggestionOptions}
        endAdornment={
          <SearchAction
            disabled={loading || results.state === 'loading' || !query}
            onClick={onSearch}
            name={'Search'}
          />
        }
        onBackToResults={onBackToResults}
        backToResults={showBackToResults}
      />
    </Wrapper>
  )
}
